import React, { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router';
import PropTypes from 'prop-types';
import _ from 'lodash';
import dayjs from 'dayjs';
import buddhistEra from 'dayjs/plugin/buddhistEra';
import 'dayjs/locale/th';
import {
  Box,
  Typography,
  Button,
  LinearProgress,
  Skeleton,
} from '@mui/material';
import ArchiveIcon from '@mui/icons-material/Archive';
import Loading from 'components/Loading';
import { DateRow } from '../../components/accordion';
import * as actions from '../../redux/actions';
import { ACTIVE_STATUSES } from '../../utils/constants/financeStatus.constant';

dayjs.extend(buddhistEra);
dayjs.locale('th');

// ─── Main view ───────────────────────────────────────────────────────────────
export default function FinanceSummary({ title }) {
  const dispatch = useDispatch();
  const history = useHistory();
  const payments = useSelector((state) => state.payments);
  const expenseRows = useSelector((state) => state.expenses?.rows || []);

  useEffect(() => {
    dispatch(actions.paymentAll({ size: 9999, page: 1 }));
    dispatch(actions.budgetAll({}));
    dispatch(actions.expensesAll({ size: 9999, page: 1 }));
    dispatch(actions.meGet());
    dispatch(actions.getInformation());
  }, []);

  const hasPaymentData = !!(payments?.rows?.length);
  if (payments?.isLoading && !hasPaymentData) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="text" width="30%" height={32} sx={{ mb: 2 }} />
        {Array(5).fill(0).map((_, i) => (
          <Skeleton key={i} variant="rectangular" height={56} sx={{ mb: 1, borderRadius: 1 }} />
        ))}
      </Box>
    );
  }

  const todayKey = dayjs().format('YYYY-MM-DD');
  const ACTIVE_SET = new Set(ACTIVE_STATUSES.split(','));

  // Payment ที่ยังไม่เป็น SUCCESS — ใช้สำหรับแสดง PAYMENT DOCUMENTS ใน DateRow
  const summaryPayments = (payments?.rows || []).filter((p) => p.status !== 'SUCCESS');

  // expenses grouped by OWN date — ไม่สนใจว่าอยู่ใน payment ไหน
  // ทำให้แต่ละ expense ปรากฏใน DateRow ของวันตัวเองเสมอ ไม่ตามวันของ payment
  const expensesByDate = _.groupBy(expenseRows, (e) =>
    dayjs(e.date || e.createdAt).format('YYYY-MM-DD'),);

  const expenseIdSetByDate = _.mapValues(expensesByDate, (exps) => new Set(exps.map((e) => String(e._id))));

  // paymentsForDate — payments ที่ datePayment = dateKey หรือมี expense ที่ date = dateKey อยู่ด้วย
  const paymentsForDate = (dateKey) => {
    const seen = new Set();
    const result = [];
    summaryPayments.forEach((p) => {
      if (seen.has(p._id)) return;
      const pDate = dayjs(p.datePayment || p.date).format('YYYY-MM-DD');
      if (pDate === dateKey) { seen.add(p._id); result.push(p); return; }
      const idSet = expenseIdSetByDate[dateKey];
      if (idSet && (p.expenses_list || []).some((e) => idSet.has(String(e.expenses_id)))) {
        seen.add(p._id); result.push(p);
      }
    });
    return result;
  };

  // วันในอดีต + วันนี้ — ตัดวันในอนาคตออก
  const paymentDates = summaryPayments.map((p) => dayjs(p.datePayment || p.date).format('YYYY-MM-DD'));
  const pastDatesWithPayments = [...new Set(paymentDates)].filter((d) => d <= todayKey);
  const pastDatesWithExpenses = Object.keys(expensesByDate).filter((d) => d <= todayKey);
  const allDates = [...new Set([...pastDatesWithPayments, ...pastDatesWithExpenses, todayKey])].sort((a, b) => b.localeCompare(a));

  // วันนี้ → แสดงเสมอ
  // วันก่อนหน้า → แสดงเฉพาะที่ยังมี active expenses ค้างอยู่ (ตาม expense's own date)
  const sortedDates = allDates.filter((dateKey) => {
    if (dateKey === todayKey) return true;
    return (expensesByDate[dateKey] || []).some((e) => ACTIVE_SET.has(e.status));
  });

  return (
    <Box>
      {payments?.isLoading && hasPaymentData && (
        <LinearProgress sx={{ position: 'fixed', top: 0, left: 0, right: 0, zIndex: 9999, height: 2 }} />
      )}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <Typography variant="h5" fontWeight={700}>{title}</Typography>
        <Button
          variant="outlined"
          size="small"
          startIcon={<ArchiveIcon />}
          onClick={() => history.push('/finance/summary/archive')}
          sx={{ borderRadius: 1.5 }}
        >
          ดูประวัติ (Archive)
        </Button>
      </Box>

      {sortedDates.map((dateKey) => (
        <DateRow
          key={dateKey}
          dateKey={dateKey}
          paymentsOnDate={paymentsForDate(dateKey)}
          orphanExpenseIds={(expensesByDate[dateKey] || []).map((e) => String(e._id))}
        />
      ))}
    </Box>
  );
}

FinanceSummary.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
};
